/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import Box from "components/generals/Box";
import PricingTable from "components/tables/PricingTable";
import LoadingOverlay from "components/generals/LoadingOverlay";
import { CoreAPI_URLs } from "constants/http.const";
import useHttp from "hooks/http.hook";
import { PricingInterface, QueryResultInterface } from "medicalsystem-shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PricingExcelUpload from "components/generals/PricingExcelUpload";
import { useDispatch } from "react-redux";
import EventSlice, { MessageType } from "../../store/slices/event.slice";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { EntityPermissions, PermissionsUser } from "types";
import { useResetPageOnFilter } from "hooks/tableReset.hook";

/**
 * Content component for displaying and managing pricing information.
 * Shows a table of all pricing entries with search, filtering, pagination,
 * and Excel upload functionality for bulk pricing updates.
 * @returns {React.ReactElement} The pricing data content component
 */
const PricingDataContent: React.FC = () => {
  const [isLoading, _error, sendRequest] = useHttp();
  const [pricingList, setPricingList] = useState<PricingInterface[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowsTotal, setRowsTotal] = useState<number>(0);
  const [filterModel, setFilterModel] = useState<string>("");
  const { t } = useTranslation();
  const [isExcelUploading, setIsExcelUploading] = useState(false);
  const dispatch = useDispatch();

  const fetchPricings = () => {
    const nameParam = filterModel ? `name=${filterModel}&` : "";
    const url = `${CoreAPI_URLs.PRICING_GET_ALL}?${nameParam}page=${page}&pageSize=${pageSize}`;
    sendRequest(url, onGetPricingHandler, "GET");
  };

  useEffect(() => {
    fetchPricings();
  }, [page, pageSize, filterModel]);

  useResetPageOnFilter(filterModel, page, setPage);

  const onGetPricingHandler = (pricingsResponse: QueryResultInterface<PricingInterface>) => {
    setPricingList(pricingsResponse.results);
    setRowsTotal(pricingsResponse.total);
    setPageTotal(Math.ceil(pricingsResponse.total / pageSize));
  };

  const handleDataUpdated = (updated: boolean) => {
    if (updated) {
      fetchPricings();
    }
    setIsExcelUploading(false);
    dispatch(EventSlice.actions.resetLoadingMessages());
  };

  const handleDataUploadStart = () => {
    setIsExcelUploading(true);
    dispatch(EventSlice.actions.addMessage({
      messageType: MessageType.LOADING,
      message: t("pricingExcelUpload.uploading"),
      id: crypto.randomUUID(),
    }));
  };

  return (
    <>
      {isExcelUploading && <LoadingOverlay message={t("pricingExcelUpload.uploading")} />}
      <Box>
        <PermissionWrapper requiredPermissions={[PermissionsUser[EntityPermissions.PRICING].EDIT]}>
          <PricingExcelUpload
            setIsDataUpdated={handleDataUpdated}
            onUploadStart={handleDataUploadStart}
          />
        </PermissionWrapper>
        <PricingTable
          modelList={pricingList}
          setModelList={setPricingList}
          canSearchPlaceholder={t("pricingTable.searchPricing")}
          entriesPerPage={{ defaultValue: pageSize }}
          setPageState={setPage}
          setPageSizeState={setPageSize}
          apiPagination={true}
          apiPageTotal={pageTotal}
          apiRowsTotal={rowsTotal}
          apiPageCurrentIndex={page}
          setModelFilter={setFilterModel}
          isLoading={isLoading}
        />
      </Box>
    </>
  );
};

export default PricingDataContent;